import { combinationKey } from './options.ts';

export type OptionAxis = { optionId: string; valueIds: string[] };

export type VariantRow = {
  variantId: string | null;
  valueIds: string[];
  valueCodes: string[];
  enabled: boolean;
  sku: string;
  barcode: string;
  price: string;
  cost: string;
};

export type ValueCode = (valueId: string) => string;
export type OptionOfValue = (valueId: string) => string | undefined;

export const emptyVariantRow = (valueIds: string[] = [], valueCodes: string[] = []): VariantRow => ({
  variantId: null,
  valueIds,
  valueCodes,
  enabled: true,
  sku: '',
  barcode: '',
  price: '',
  cost: '',
});

export const combinations = (axes: readonly OptionAxis[]): string[][] =>
  axes
    .filter((axis) => axis.valueIds.length > 0)
    .reduce<string[][]>(
      (prefixes, axis) => prefixes.flatMap((prefix) => axis.valueIds.map((id) => [...prefix, id])),
      [[]],
    );

const optionlessHeir = (rows: readonly VariantRow[]): VariantRow | undefined => {
  const [only] = rows;
  const isSavedSingle = rows.length === 1 && only?.valueIds.length === 0 && only.variantId !== null;
  return isSavedSingle ? only : undefined;
};

export const reconcileVariants = (
  rows: readonly VariantRow[],
  axes: readonly OptionAxis[],
  codeOf: ValueCode,
): VariantRow[] => {
  const wanted = combinations(axes);
  const byKey = new Map(rows.map((row) => [combinationKey(row.valueIds), row]));
  const heir = optionlessHeir(rows);
  const matched = wanted.map((valueIds) => byKey.get(combinationKey(valueIds)));
  const inheritsFirst = heir !== undefined && wanted.length > 0 && matched.every((row) => row === undefined);
  if (wanted.length === 1 && wanted[0]?.length === 0)
    return [byKey.get('') ?? heir ?? rows[0] ?? emptyVariantRow()].map((row) => ({
      ...row,
      valueIds: [],
      valueCodes: [],
    }));
  return wanted.map((valueIds, index) => {
    const valueCodes = valueIds.map(codeOf);
    const existing = matched[index];
    if (existing !== undefined) return { ...existing, valueIds, valueCodes };
    if (inheritsFirst && index === 0) return { ...heir, valueIds, valueCodes };
    return emptyVariantRow(valueIds, valueCodes);
  });
};

export const autoSku = (reference: string, valueCodes: readonly string[]): string => {
  const base = reference.trim();
  if (base === '') return '';
  return [base, ...valueCodes.map((code) => code.toUpperCase())].join('-');
};

export const effectiveSku = (row: VariantRow, reference: string): string =>
  row.sku.trim() === '' ? autoSku(reference, row.valueCodes) : row.sku.trim();

export const axesOf = (
  valueIdsPerVariant: readonly (readonly string[])[],
  optionOf: OptionOfValue,
): OptionAxis[] => {
  const axes = new Map<string, string[]>();
  for (const valueId of valueIdsPerVariant.flat()) {
    const optionId = optionOf(valueId);
    if (optionId === undefined) continue;
    const values = axes.get(optionId) ?? [];
    if (!values.includes(valueId)) values.push(valueId);
    axes.set(optionId, values);
  }
  return [...axes].map(([optionId, valueIds]) => ({ optionId, valueIds }));
};
